"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UNTESFromFlightEffects = void 0;
const zod_1 = require("zod");
const serialised_eventr_date_1 = require("./serialised-eventr-date");
const start_or_end_enum_1 = require("./start-or-end-enum");
const FlightSchema = zod_1.z.object({
    _id: zod_1.z.string(),
    passengers_list: zod_1.z
        .array(zod_1.z.object({
        booking_ref: zod_1.z.string().optional(),
        users: zod_1.z
            .array(zod_1.z.object({
            option: zod_1.z.union([zod_1.z.string(), zod_1.z.number().int()]),
        }))
            .optional(),
    }))
        .optional(),
    departure: zod_1.z.object({
        dep_time: serialised_eventr_date_1.EventrDateFromStringSchema,
        dep_terminal: zod_1.z.string().optional(),
        dep_gate: zod_1.z.string().optional(),
        dep_name: zod_1.z.string().optional(),
        dep_short_name: zod_1.z.string().optional(),
        revisedTime: serialised_eventr_date_1.EventrDateFromStringSchema.optional(),
    }),
    arrival: zod_1.z.object({
        arr_time: serialised_eventr_date_1.EventrDateFromStringSchema,
        arr_name: zod_1.z.string().optional(),
        arr_short_name: zod_1.z.string().optional(),
        arr_gate: zod_1.z.string().optional(),
        arr_terminal: zod_1.z.string().optional(),
    }),
    bookref: zod_1.z.string().optional(),
    outboundAirportAbr: zod_1.z.string(),
    inboundAirportAbr: zod_1.z.string(),
    airline: zod_1.z.string().optional(),
    check_in: serialised_eventr_date_1.EventrDateFromStringSchema.optional(),
    startOrEnd: start_or_end_enum_1.StartOrEndEnum,
});
/**
 * A ZodEffects that transforms the flight data into a UNTES.
 * @param userId The user ID to find the correct passenger group to source the
 * booking reference.
 * @returns The UNTES object.
 */
const UNTESFromFlightEffects = (userId) => FlightSchema.transform((flight) => {
    const passengerGroupElement = flight.passengers_list.find((passengerGroup) => passengerGroup.users.map((user) => user.option).includes(userId));
    return {
        passengerGroup: passengerGroupElement,
        ...flight,
    };
})
    .superRefine((flight, ctx) => {
    if (!flight.passengerGroup) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            message: `No passenger group found for user ${userId}`,
        });
        return zod_1.z.NEVER;
    }
})
    .transform((flight) => {
    const commonProps = {
        id: flight._id,
        section: 'flight',
        tertiary: [flight.bookref, flight.airline]
            .filter(Boolean)
            .join(' ') || undefined,
        ...(flight.passengerGroup.booking_ref
            ? { bookingReference: flight.passengerGroup.booking_ref }
            : {}),
        startOrEnd: flight.startOrEnd,
    };
    switch (flight.startOrEnd) {
        case 'start':
            const startSecondary = [
                flight.departure.dep_terminal &&
                    `Terminal ${flight.departure.dep_terminal}`,
                flight.departure.dep_gate &&
                    `Gate ${flight.departure.dep_gate}`,
            ]
                .filter(Boolean)
                .join(', ') || undefined;
            const startEntryProps = {
                ...(flight.check_in
                    ? { checkIn: flight.check_in }
                    : {}),
                date: flight.departure.revisedTime ??
                    flight.departure.dep_time,
                primary: flight.departure.dep_short_name ??
                    flight.departure.dep_name,
                ...(startSecondary ? { startSecondary } : {}),
            };
            return {
                ...commonProps,
                ...startEntryProps,
            };
        case 'end':
            const endSecondary = [
                flight.arrival.arr_terminal &&
                    `Terminal ${flight.arrival.arr_terminal}`,
                flight.arrival.arr_gate &&
                    `Gate ${flight.arrival.arr_gate}`,
            ]
                .filter(Boolean)
                .join(', ') || undefined;
            const endEntryProps = {
                date: flight.arrival.arr_time,
                primary: flight.arrival.arr_short_name ??
                    flight.arrival.arr_name,
                ...(endSecondary ? { secondary: endSecondary } : {}),
            };
            return {
                ...commonProps,
                ...endEntryProps,
            };
    }
});
exports.UNTESFromFlightEffects = UNTESFromFlightEffects;
